<template>
  <section class="estrut-materials">
    <header class="estrut-materials__hero">
      <div class="estrut-materials__hero-main">
        <div class="estrut-materials__meta">
          <div class="estrut-materials__icon-wrap">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
          </div>
          <div class="estrut-materials__meta-text">
            <h3 class="estrut-materials__title">{{ t('estruturacao.lessons.materialsTitle') }}</h3>
            <p class="estrut-materials__subtitle">{{ t('estruturacao.lessons.materialsSubtitle') }}</p>
          </div>
        </div>

        <div v-if="editMode" class="estrut-materials__actions">
          <button type="button" class="estrut-materials__btn estrut-materials__btn--link" @click="openLinkModal">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            {{ t('estruturacao.lessons.addLink') }}
          </button>
          <button
            type="button"
            class="estrut-materials__btn estrut-materials__btn--upload"
            :disabled="uploading"
            @click="openFileDialog"
          >
            <svg v-if="uploading" class="estrut-materials__btn-spinner" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.25" />
              <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <svg v-else fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            {{ uploading ? t('estruturacao.lessons.uploading') : t('estruturacao.lessons.addFile') }}
          </button>
          <input ref="fileInput" type="file" class="hidden" accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar" @change="handleFile" />
        </div>
      </div>
    </header>

    <div class="estrut-materials__body">

    <div v-if="!materiais.length" class="estrut-materials__empty">
      {{ t('estruturacao.lessons.noMaterials') }}
    </div>

    <ul v-else class="estrut-materials__list">
      <li
        v-for="item in materiais"
        :key="item.id"
        class="estrut-materials__item"
        :class="item.tipo === 'LINK' ? 'estrut-materials__item--link' : 'estrut-materials__item--file'"
      >
        <div
          class="estrut-materials__item-icon"
          :class="getItemIconClass(item)"
        >
          <svg v-if="item.tipo === 'LINK'" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          <svg v-else-if="isPdfItem(item)" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M14 3v4a2 2 0 002 2h4M9 13h6M9 17h4" />
          </svg>
          <svg v-else fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>

        <div class="estrut-materials__item-body">
          <div class="estrut-materials__item-head">
            <span class="estrut-materials__item-title">{{ item.titulo }}</span>
            <span class="estrut-materials__item-badge">
              {{ item.tipo === 'LINK' ? t('estruturacao.lessons.materialTypeLink') : getFileExtension(item) }}
            </span>
          </div>
          <p v-if="hasItemMeta(item)" class="estrut-materials__item-meta">
            <template v-if="item.tipo === 'LINK'">{{ truncateUrl(item.url) }}</template>
            <template v-else>
              <span v-if="showFileName(item)">{{ item.nomeArquivo }}</span>
              <span v-if="item.tamanhoBytes" class="estrut-materials__item-size">{{ formatFileSize(item.tamanhoBytes) }}</span>
            </template>
          </p>
        </div>

        <div class="estrut-materials__item-actions">
          <button
            v-if="item.tipo === 'LINK'"
            type="button"
            class="estrut-materials__icon-btn estrut-materials__icon-btn--primary"
            :title="t('estruturacao.lessons.openLink')"
            :aria-label="t('estruturacao.lessons.openLink')"
            @click="openLink(item.url)"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            <span class="estrut-materials__icon-btn-label">{{ t('estruturacao.lessons.openLink') }}</span>
          </button>
          <button
            v-else
            type="button"
            class="estrut-materials__icon-btn estrut-materials__icon-btn--primary"
            :title="t('estruturacao.lessons.download')"
            :aria-label="t('estruturacao.lessons.download')"
            @click="downloadFile(item)"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span class="estrut-materials__icon-btn-label">{{ t('estruturacao.lessons.download') }}</span>
          </button>
          <button
            v-if="editMode"
            type="button"
            class="estrut-materials__icon-btn estrut-materials__icon-btn--danger"
            :title="t('common.remove')"
            :aria-label="t('common.remove')"
            @click="removeItem(item)"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            <span class="estrut-materials__icon-btn-label">{{ t('common.remove') }}</span>
          </button>
        </div>
      </li>
    </ul>
    </div>

    <EstruturacaoMaterialLinkModal
      ref="linkModalRef"
      :open="linkModalOpen"
      @close="closeLinkModal"
      @submit="onLinkSubmit"
    />
  </section>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import Swal from 'sweetalert2'
import { addMaterialLink, uploadMaterial, deleteMaterial, downloadMaterial } from '../../services/estruturacaoApi'
import EstruturacaoMaterialLinkModal from './EstruturacaoMaterialLinkModal.vue'

const props = defineProps({
  materiais: { type: Array, default: () => [] },
  aulaId: { type: Number, required: true },
  editMode: { type: Boolean, default: false }
})

const emit = defineEmits(['refresh'])

const { t } = useI18n()
const fileInput = ref(null)
const uploading = ref(false)
const linkModalOpen = ref(false)
const linkModalRef = ref(null)

function openLinkModal() {
  linkModalOpen.value = true
}

function closeLinkModal() {
  linkModalOpen.value = false
}

async function onLinkSubmit(data) {
  try {
    await addMaterialLink(props.aulaId, data)
    closeLinkModal()
    emit('refresh')
  } catch (e) {
    linkModalRef.value?.setError(e.response?.data?.error || t('estruturacao.lessons.saveError'))
  } finally {
    linkModalRef.value?.setSaving(false)
  }
}

function openFileDialog() {
  fileInput.value?.click()
}

function getFileExtension(item) {
  const name = item.nomeArquivo || item.titulo || ''
  const ext = name.split('.').pop()?.toUpperCase()
  return ext && ext.length <= 8 ? ext : t('estruturacao.lessons.materialTypeFile')
}

function isPdfItem(item) {
  const name = (item.nomeArquivo || item.titulo || '').toLowerCase()
  return name.endsWith('.pdf')
}

function getItemIconClass(item) {
  if (item.tipo === 'LINK') return 'estrut-materials__item-icon--link'
  if (isPdfItem(item)) return 'estrut-materials__item-icon--pdf'
  return 'estrut-materials__item-icon--file'
}

function showFileName(item) {
  return item.nomeArquivo && item.nomeArquivo !== item.titulo
}

function hasItemMeta(item) {
  if (item.tipo === 'LINK') return !!item.url
  return showFileName(item) || !!item.tamanhoBytes
}

function formatFileSize(bytes) {
  if (!bytes || bytes <= 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function truncateUrl(url) {
  if (!url) return ''
  try {
    const u = new URL(url)
    const path = u.hostname + u.pathname
    return path.length > 48 ? `${path.slice(0, 45)}…` : path
  } catch {
    return url.length > 48 ? `${url.slice(0, 45)}…` : url
  }
}

function openLink(url) {
  if (url) window.open(url, '_blank', 'noopener,noreferrer')
}

async function downloadFile(item) {
  try {
    await downloadMaterial(item.id, item.nomeArquivo || item.titulo)
  } catch (e) {
    Swal.fire(t('common.error'), t('estruturacao.lessons.downloadError'), 'error')
  }
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const base64 = reader.result.split(',')[1]
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

async function handleFile(event) {
  const file = event.target.files?.[0]
  if (!file) return
  uploading.value = true
  try {
    const base64 = await fileToBase64(file)
    await uploadMaterial(props.aulaId, {
      titulo: file.name,
      nomeOriginal: file.name,
      tipoArquivo: file.type,
      tamanho: file.size,
      conteudoBase64: base64
    })
    emit('refresh')
  } catch (e) {
    Swal.fire(t('common.error'), e.response?.data?.error || t('estruturacao.lessons.uploadError'), 'error')
  } finally {
    uploading.value = false
    if (fileInput.value) fileInput.value.value = ''
  }
}

async function removeItem(item) {
  const result = await Swal.fire({
    title: t('estruturacao.lessons.deleteMaterialConfirm'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('common.yes'),
    cancelButtonText: t('common.cancel')
  })
  if (!result.isConfirmed) return
  try {
    await deleteMaterial(item.id)
    emit('refresh')
  } catch (e) {
    Swal.fire(t('common.error'), e.response?.data?.error || t('estruturacao.lessons.deleteError'), 'error')
  }
}
</script>

<style scoped>
.estrut-materials {
  border-radius: 4px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  overflow: hidden;
  box-shadow: 0 4px 24px color-mix(in srgb, #000 6%, transparent);
}

.estrut-materials__hero {
  padding: 18px 20px;
  border-bottom: 1px solid var(--border-primary);
  background: linear-gradient(
    135deg,
    color-mix(in srgb, #7c3aed 8%, var(--bg-primary)),
    var(--bg-primary)
  );
}

.estrut-materials__hero-main {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.estrut-materials__meta {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.estrut-materials__icon-wrap {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background: linear-gradient(
    135deg,
    color-mix(in srgb, #7c3aed 85%, #db2777),
    color-mix(in srgb, #db2777 75%, #7c3aed)
  );
  color: #fff;
  box-shadow: 0 4px 12px color-mix(in srgb, #7c3aed 28%, transparent);
}

.estrut-materials__icon-wrap svg {
  width: 22px;
  height: 22px;
}

.estrut-materials__meta-text {
  flex: 1;
  min-width: 0;
  padding-top: 2px;
}

.estrut-materials__title {
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--text-primary);
}

.estrut-materials__subtitle {
  margin: 4px 0 0;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: var(--text-secondary);
}

.estrut-materials__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.estrut-materials__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 9px 16px;
  font-size: 0.8125rem;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid transparent;
  cursor: pointer;
  white-space: nowrap;
  transition: transform 0.15s, box-shadow 0.15s, background 0.15s, border-color 0.15s;
}

.estrut-materials__btn svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.estrut-materials__btn--link {
  border-color: var(--border-secondary, var(--border-primary));
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.estrut-materials__btn--link:hover:not(:disabled) {
  border-color: color-mix(in srgb, var(--accent-primary) 35%, var(--border-primary));
  background: color-mix(in srgb, var(--accent-primary) 6%, var(--bg-secondary));
  color: var(--accent-primary);
}

.estrut-materials__btn--upload {
  background: linear-gradient(
    135deg,
    var(--accent-primary),
    color-mix(in srgb, var(--accent-primary) 72%, #7c3aed)
  );
  color: #fff;
  box-shadow: 0 4px 14px color-mix(in srgb, var(--accent-primary) 30%, transparent);
}

.estrut-materials__btn--upload:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px color-mix(in srgb, var(--accent-primary) 38%, transparent);
}

.estrut-materials__btn:disabled {
  opacity: 0.65;
  cursor: wait;
  transform: none;
}

.estrut-materials__btn-spinner {
  animation: estrut-mat-spin 0.75s linear infinite;
}

.estrut-materials__body {
  padding: 16px 20px 20px;
}

.estrut-materials__uploading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  margin-bottom: 12px;
  border-radius: 4px;
  background: color-mix(in srgb, var(--accent-primary) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--accent-primary) 18%, transparent);
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.estrut-materials__spinner {
  width: 18px;
  height: 18px;
  border: 2px solid var(--border-primary);
  border-top-color: var(--accent-primary);
  border-radius: 50%;
  animation: estrut-mat-spin 0.7s linear infinite;
}

@keyframes estrut-mat-spin {
  to { transform: rotate(360deg); }
}

.estrut-materials__empty {
  padding: 28px 16px;
  text-align: center;
  font-size: 0.875rem;
  color: var(--text-secondary);
  border-radius: 4px;
  border: 1px dashed var(--border-primary);
  background: var(--bg-secondary);
}

.estrut-materials__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.estrut-materials__item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 4px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
  overflow: hidden;
}

.estrut-materials__item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: var(--accent-primary);
}

.estrut-materials__item--link::before {
  background: linear-gradient(180deg, #3b82f6, #6366f1);
}

.estrut-materials__item--file::before {
  background: linear-gradient(180deg, #f59e0b, #ea580c);
}

.estrut-materials__item:hover {
  border-color: color-mix(in srgb, var(--accent-primary) 25%, var(--border-primary));
  box-shadow: 0 4px 16px color-mix(in srgb, #000 6%, transparent);
}

.estrut-materials__item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  margin-left: 4px;
  border-radius: 4px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
}

.estrut-materials__item-icon svg {
  width: 22px;
  height: 22px;
}

.estrut-materials__item-icon--link {
  color: #3b82f6;
  background: color-mix(in srgb, #3b82f6 10%, var(--bg-primary));
  border-color: color-mix(in srgb, #3b82f6 22%, transparent);
}

.estrut-materials__item-icon--pdf {
  color: #dc2626;
  background: color-mix(in srgb, #dc2626 10%, var(--bg-primary));
  border-color: color-mix(in srgb, #dc2626 22%, transparent);
}

.estrut-materials__item-icon--file {
  color: var(--accent-primary);
  background: color-mix(in srgb, var(--accent-primary) 8%, var(--bg-primary));
  border-color: color-mix(in srgb, var(--accent-primary) 18%, transparent);
}

.estrut-materials__item-body {
  flex: 1;
  min-width: 0;
}

.estrut-materials__item-head {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}

.estrut-materials__item-title {
  font-size: 0.9375rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.estrut-materials__item-badge {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-secondary);
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
}

.estrut-materials__item--link .estrut-materials__item-badge {
  color: #3b82f6;
  border-color: color-mix(in srgb, #3b82f6 25%, transparent);
  background: color-mix(in srgb, #3b82f6 8%, var(--bg-primary));
}

.estrut-materials__item--file .estrut-materials__item-badge {
  color: #ea580c;
  border-color: color-mix(in srgb, #ea580c 25%, transparent);
  background: color-mix(in srgb, #f59e0b 8%, var(--bg-primary));
}

.estrut-materials__item-meta {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.45;
  color: var(--text-tertiary);
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.estrut-materials__item-size {
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
}

.estrut-materials__item-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.estrut-materials__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid transparent;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s, background 0.15s, border-color 0.15s, color 0.15s;
}

.estrut-materials__icon-btn svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.estrut-materials__icon-btn-label {
  white-space: nowrap;
}

.estrut-materials__icon-btn--primary {
  color: var(--accent-primary);
  background: color-mix(in srgb, var(--accent-primary) 10%, var(--bg-primary));
  border-color: color-mix(in srgb, var(--accent-primary) 28%, transparent);
}

.estrut-materials__icon-btn--primary:hover {
  color: #fff;
  background: var(--accent-primary);
  border-color: var(--accent-primary);
  box-shadow: 0 4px 12px color-mix(in srgb, var(--accent-primary) 35%, transparent);
  transform: translateY(-1px);
}

.estrut-materials__icon-btn--danger {
  color: #dc2626;
  background: color-mix(in srgb, #dc2626 8%, var(--bg-primary));
  border-color: color-mix(in srgb, #dc2626 25%, transparent);
}

.estrut-materials__icon-btn--danger:hover {
  color: #fff;
  background: #dc2626;
  border-color: #dc2626;
  box-shadow: 0 4px 12px color-mix(in srgb, #dc2626 30%, transparent);
  transform: translateY(-1px);
}

@media (max-width: 640px) {
  .estrut-materials__icon-btn-label {
    display: none;
  }

  .estrut-materials__icon-btn {
    padding: 8px 10px;
  }

  .estrut-materials__item-actions {
    flex-direction: column;
    gap: 6px;
  }
}

.hidden {
  display: none;
}

@media (max-width: 768px) {
  .estrut-materials__hero-main {
    flex-direction: column;
    align-items: stretch;
  }

  .estrut-materials__actions {
    width: 100%;
  }

  .estrut-materials__btn {
    flex: 1;
    min-width: 0;
  }
}
</style>
